/**
 * Emergency contacts: add / edit / remove, choose the primary one (the first, big one on the call
 * sheet), and call any of them. Numbers show masked until the patient asks to see one; they are
 * stored encrypted and visible only to this patient.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import {
  addEmergencyContact,
  emergencyContactsKey,
  listEmergencyContacts,
  removeEmergencyContact,
  setPrimaryEmergencyContact,
  updateEmergencyContact,
  type ContactRelationship,
  type EmergencyContact,
  type EmergencyContactInput,
} from "../../api/emergencyContacts";
import { describeError } from "../../api/errors";
import { useAuth } from "../../auth/AuthContext";
import { EmergencyCallDialog, PhoneIcon } from "../../components/EmergencyButton";
import { EditIcon, IconButton, RowActions, TrashIcon } from "../../components/ui/IconButton";
import { Modal } from "../../components/ui/Modal";
import { PhoneInput } from "../../components/ui/PhoneInput";
import { formatPhone } from "../../lib/dial";
import { RELATIONSHIP_LABELS, relationshipText } from "../../lib/emergencyContacts";

// Mirrors the backend's MAX_EMERGENCY_CONTACTS default; the API has the final say.
const MAX_CONTACTS = 5;
const RELATIONSHIPS = Object.keys(RELATIONSHIP_LABELS) as ContactRelationship[];

export function EmergencyContacts() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const key = emergencyContactsKey(user?.id);
  const { data: contacts = [], isLoading } = useQuery({ queryKey: key, queryFn: listEmergencyContacts });
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<EmergencyContact | null>(null);
  const [removing, setRemoving] = useState<EmergencyContact | null>(null);
  const [calling, setCalling] = useState<EmergencyContact | null>(null);
  const [shown, setShown] = useState<Set<string>>(new Set());

  const makePrimary = useMutation({
    mutationFn: setPrimaryEmergencyContact,
    onSuccess: (c) => {
      toast.success(`${c.name} is now your primary contact.`);
      invalidate();
    },
    onError: (err) => toast.error(describeError(err)),
  });
  const remove = useMutation({
    mutationFn: removeEmergencyContact,
    onSuccess: () => {
      setRemoving(null);
      invalidate();
    },
    onError: (err) => toast.error(describeError(err)),
  });

  const toggleShown = (id: string) =>
    setShown((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const atLimit = contacts.length >= MAX_CONTACTS;

  return (
    <div className="max-w-4xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Emergency contacts</h2>
          <p className="text-sm text-gray-500">The people the Emergency button calls. Your primary contact comes first.</p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          disabled={atLimit}
          title={atLimit ? `You can save up to ${MAX_CONTACTS} contacts.` : undefined}
          className="btn-primary"
        >
          + Add contact
        </button>
      </div>

      <div className="panel flex items-start gap-3 text-sm text-ink-soft">
        <span aria-hidden="true">🔒</span>
        <span>
          Only you can see these contacts. Numbers are stored encrypted and stay hidden on screen until you choose to
          show one. We always ask before calling.
        </span>
      </div>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="skeleton h-36 rounded-2xl" />
          <div className="skeleton h-36 rounded-2xl" />
        </div>
      ) : contacts.length === 0 ? (
        <div className="empty-state">
          <p className="font-medium text-ink">No emergency contacts yet</p>
          <p className="text-sm text-ink-muted">Add a family member, friend or your doctor so help is one tap away.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {contacts.map((c) => (
            <div key={c.id} className={`card p-4 ${c.is_primary ? "ring-2 ring-danger/30" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-semibold text-ink">{c.name}</span>
                    {c.is_primary && <span className="badge-danger">Primary</span>}
                  </div>
                  <div className="text-sm text-ink-soft">{relationshipText(c)}</div>
                </div>
                <RowActions>
                  <IconButton label="Edit" tone="brand" onClick={() => { setEditing(c); setFormOpen(true); }}>
                    <EditIcon />
                  </IconButton>
                  <IconButton label="Remove" tone="danger" onClick={() => setRemoving(c)}>
                    <TrashIcon />
                  </IconButton>
                </RowActions>
              </div>

              <div className="mt-3 flex items-center gap-2">
                <span className="font-mono text-sm text-ink">{shown.has(c.id) ? formatPhone(c.phone) : c.phone_masked}</span>
                <button
                  type="button"
                  onClick={() => toggleShown(c.id)}
                  className="text-xs font-medium text-brand hover:underline"
                  aria-label={shown.has(c.id) ? `Hide ${c.name}'s number` : `Show ${c.name}'s number`}
                >
                  {shown.has(c.id) ? "Hide" : "Show"}
                </button>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={() => setCalling(c)} className="btn-danger btn-sm">
                  <PhoneIcon className="h-3.5 w-3.5" />
                  Call
                </button>
                {!c.is_primary && (
                  <button
                    type="button"
                    onClick={() => makePrimary.mutate(c.id)}
                    disabled={makePrimary.isPending}
                    className="btn-secondary btn-sm"
                  >
                    Make primary
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={formOpen} title={editing ? "Edit contact" : "Add emergency contact"} onClose={() => setFormOpen(false)}>
        <ContactForm
          initial={editing}
          isFirst={contacts.length === 0}
          onCancel={() => setFormOpen(false)}
          onSaved={() => {
            setFormOpen(false);
            invalidate();
          }}
        />
      </Modal>

      <Modal open={removing !== null} title="Remove contact?" onClose={() => setRemoving(null)}>
        {removing && (
          <div className="space-y-4">
            <p className="text-sm text-ink-soft">
              {removing.name} will no longer be on your emergency call list
              {removing.is_primary && contacts.length > 1 ? ", and your next contact becomes the primary one" : ""}.
            </p>
            <div className="flex gap-2">
              <button className="btn-danger" disabled={remove.isPending} onClick={() => remove.mutate(removing.id)}>
                {remove.isPending ? "Removing…" : "Remove"}
              </button>
              <button className="btn-secondary" onClick={() => setRemoving(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>

      <EmergencyCallDialog open={calling !== null} onClose={() => setCalling(null)} only={calling ?? undefined} />
    </div>
  );
}

function ContactForm({
  initial,
  isFirst,
  onSaved,
  onCancel,
}: {
  initial: EmergencyContact | null;
  isFirst: boolean;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<EmergencyContactInput>({
    name: initial?.name ?? "",
    phone: initial?.phone ?? "+91",
    relationship: initial?.relationship ?? "family",
    relationship_label: initial?.relationship_label ?? "",
    is_primary: false,
  });
  const [error, setError] = useState<string | null>(null);
  const set = (p: Partial<EmergencyContactInput>) => setForm({ ...form, ...p });

  const save = useMutation({
    mutationFn: () => {
      const body = { ...form, name: form.name.trim(), relationship_label: form.relationship_label?.trim() || null };
      if (initial) {
        // Primary is switched from the list (Make primary), not by editing.
        const { name, phone, relationship, relationship_label } = body;
        return updateEmergencyContact(initial.id, { name, phone, relationship, relationship_label });
      }
      return addEmergencyContact(body);
    },
    onSuccess: (c) => {
      toast.success(initial ? "Contact updated." : `${c.name} added to your emergency contacts.`);
      onSaved();
    },
    onError: (err) => setError(describeError(err)),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        save.mutate();
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          <span className="label mb-0">Name</span>
          <input value={form.name} onChange={(e) => set({ name: e.target.value })} required maxLength={80} className="input" />
        </label>
        <PhoneInput label="Mobile number" value={form.phone} onChange={(v) => set({ phone: v })} required />
        <label className="flex flex-col gap-1 text-sm">
          <span className="label mb-0">Relationship</span>
          <select
            value={form.relationship}
            onChange={(e) => set({ relationship: e.target.value as ContactRelationship })}
            className="select"
          >
            {RELATIONSHIPS.map((r) => (
              <option key={r} value={r}>
                {RELATIONSHIP_LABELS[r]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="label mb-0">Who they are (optional)</span>
          <input
            value={form.relationship_label ?? ""}
            onChange={(e) => set({ relationship_label: e.target.value })}
            placeholder="e.g. Mother, Neighbour"
            maxLength={40}
            className="input"
          />
        </label>
      </div>
      {!initial &&
        (isFirst ? (
          <p className="field-hint">Your first contact becomes your primary emergency contact.</p>
        ) : (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_primary} onChange={(e) => set({ is_primary: e.target.checked })} />
            Make this my primary emergency contact
          </label>
        ))}
      {error && <p className="field-error">{error}</p>}
      <div className="flex gap-2">
        <button className="btn-primary" disabled={save.isPending}>
          {save.isPending ? "Saving…" : initial ? "Save" : "Add contact"}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
      </div>
    </form>
  );
}
